/**
 * The scoresheets wire protocol (PLAN-SCORESHEETS S1, S4).
 *
 * Shared by the Worker (which writes these responses) and the browser (which
 * reads them), exactly like `fermentation/protocol.ts`, `share/protocol.ts` and
 * `sync/protocol.ts`. One module, so the two halves cannot drift about what a
 * sheet is.
 *
 * ```
 * public   GET    /api/taste/:token                    → { ok, card, forms[], subject }
 * public   POST   /api/taste/:token                    → { ok }
 * session  GET    /api/scoresheets/batch/:id           → { ok, sheets, scans, link }
 * session  GET    /api/scoresheets/blend/:id           → the same, about a blend
 * session  GET    /api/scoresheets/journal/:id         → the same, about a journal
 * session  POST   /api/scoresheets/batch/:id/scans     → { ok, scan }
 * session  POST   /api/scoresheets/blend/:id/scans     → the same, about a blend
 * session  POST   /api/scoresheets/journal/:id/scans   → the same, about a journal
 * session  POST   /api/scoresheets/scans/:scanId/pages      → { ok, scan }
 * session  POST   /api/scoresheets/scans/:scanId/read       → { ok, scan, sheets }
 * session  GET    /api/scoresheets/scans/:scanId/pages/:n   → the page bytes
 * session  DELETE /api/scoresheets/scans/:scanId            → { ok }
 * session  PATCH  /api/scoresheets/:id                 → { ok, sheet }
 * session  DELETE /api/scoresheets/:id                 → { ok }
 * session  POST   /api/scoresheets/batch/:id/seen      → { ok }
 * session  POST   /api/scoresheets/blend/:id/seen      → the same, about a blend
 * session  POST   /api/scoresheets/journal/:id/seen    → the same, about a journal
 * session  GET    /api/scoresheets/unseen              → { ok, counts }
 * session  DELETE /api/scoresheets/batch/:id           → { ok }
 * session  DELETE /api/scoresheets/blend/:id           → the same, about a blend
 * session  DELETE /api/scoresheets/journal/:id         → the same, about a journal
 * session  POST   /api/feedback-links                  → { ok, links[] }
 * session  POST   /api/feedback-links/:token/revoke    → { ok }
 * ```
 *
 * ## A sheet is about a batch, a blend or a journal (D-BS6, D-JL1)
 *
 * The four per-subject routes are written once in the Worker and registered
 * three times, under `/batch/:id`, `/blend/:id` and `/journal/:id`, because
 * nothing in this pipeline ever asked what a batch *is*: the mint takes a
 * document id, the public form reads a card captured at mint, the owner's read
 * is keyed on the id, and the delete sweep deletes by the id. The kind is a
 * word carried beside the id — {@link FeedbackSubjectKind} on this wire,
 * `subject` in D1 — and two readers need it: the digest mail, which has to
 * know whether to link a brewer to `/batches/<id>/feedback`,
 * `/blending/<id>/feedback` or `/journals/<id>/feedback`, and the public form,
 * which asks a taster of a mead different questions from a taster of a beer
 * (D-JL2, D-JL3).
 *
 * `batchId` keeps its name on every DTO here, matching the column that keeps
 * its name for `migrations/0033_feedback_subject.sql`'s reason: it holds the
 * subject's document id, whichever kind the subject is, and renaming a field
 * across the wire and three tables to say so would be a migration for a
 * comment's worth of truth.
 *
 * ## Why the public form lives under its own base path
 *
 * The same reason `/api/ingest` and `/api/fermentation` are two paths (see
 * `fermentation/protocol.ts`): the Worker's session middleware lets a request
 * through when its path *starts with* one of `PUBLIC_API_PREFIXES`, so the
 * anonymous, token-authorized write has to sit under a prefix that contains
 * nothing else. `/api/taste/` holds the stranger's two routes and nothing more;
 * the owner's surface is `/api/scoresheets/*` and `/api/feedback-links/*`, and
 * both are a different *word* rather than a longer form of the public one. A
 * base path named `/api/tasting-sheets` would have been a security bug the day
 * somebody wrote `startsWith('/api/tast')`.
 *
 * ## One body for every source
 *
 * A judge's sheet, a friend's sheet at a club meeting and a page read off a
 * scan are the same document with different parts filled in, so there is one
 * schema and one score function rather than three of each. The maxima and the
 * descriptor enum come from `@beerwright/commands`, which is where the brewer's
 * *own* tasting sheet gets them: a judge's 12 for aroma has to be the same 12 the
 * brewer is bounded by, or the Feedback page would be averaging two scales.
 *
 * Nothing in the body carries a `.default()`, for the reason
 * `batchTastingSchema`'s header gives at length — the owner's `PATCH` sends the
 * whole body, so nothing needs one, and a defaulted key is a trap for the first
 * `.partial()` anybody reaches for later.
 *
 * ## Units and instants
 *
 * Instants on this wire are **milliseconds** since the epoch, as everywhere else
 * in this project. The D1 columns are seconds (the house rule for that schema)
 * and the conversion happens at the edge of the Worker, exactly as `share.ts`
 * does it.
 */

import {
  BATCH_TASTING_DESCRIPTORS,
  BATCH_TASTING_MAX_TOTAL,
  BATCH_TASTING_SECTION_MAX,
  type BatchTastingSection,
  batchTastingDescriptorSchema,
} from '@beerwright/commands';
import { z } from 'zod';
// Relative, not `@/`: this module is compiled by `tsconfig.worker.json` as well
// as by the browser's config, and the Worker's compilation has no path alias.
// Same rule `fermentation/protocol.ts` and `share/protocol.ts` follow.
import type { Plan } from '../lib/plan';

// ---------------------------------------------------------------------------
// The subject
// ---------------------------------------------------------------------------

/**
 * Which kind of document a sheet, a scan or a link is about (D-BS6, D-JL1).
 *
 * Three words, and the enum is the whole of what the pipeline knows about the
 * difference. The third arrived exactly as this comment said it would when
 * there were two: a route registration and a URL builder — plus, because a
 * journal may be a cider or a hot sauce, a form that stops asking about beer
 * faults (D-JL3). That is the point of writing it this way rather than as a
 * boolean.
 *
 * A schema rather than a bare union because the mint route validates what a
 * client sent against it, the Worker reads D1's `subject` column through it
 * (`toSubjectKind`), and the wire's word and the server's word have to be the
 * same list.
 */
export const feedbackSubjectKindSchema = z.enum(['batch', 'blend', 'journal']);

/** @see feedbackSubjectKindSchema */
export type FeedbackSubjectKind = z.infer<typeof feedbackSubjectKindSchema>;

/**
 * The beer a print run, a scan or a page of sheets is about.
 *
 * The **id alone identifies the document**: all three kinds come out of one
 * document-id minter, so every query in the Worker is keyed on the id and the
 * kind never narrows it. What the kind does is pick the route — and, further
 * downstream, the page the nightly digest links to, which is the one reader
 * that needs the word at all (D-BS6, and `migrations/0033_feedback_subject.sql`
 * argues it at length). The two travel together as an object rather than as two
 * arguments so that a caller cannot get the order wrong, and so that the three
 * path builders below take one thing.
 */
export interface FeedbackSubject {
  readonly kind: FeedbackSubjectKind;
  /** The document id: a batch's, a blend's or a journal's. */
  readonly id: string;
}

// ---------------------------------------------------------------------------
// Paths
// ---------------------------------------------------------------------------

/** The owner's sheets, scans and their pages. Session-guarded, plan-gated. */
export const SCORESHEETS_BASE_PATH = '/api/scoresheets';

/** Minting and revoking the link a card carries. Session-guarded, plan-gated. */
export const FEEDBACK_LINKS_BASE_PATH = '/api/feedback-links';

/**
 * Where a stranger with a bottle posts. **The only path under `/api/` a request
 * with no session may use to write a sheet**, and it holds nothing else.
 */
export const TASTE_PUBLIC_BASE_PATH = '/api/taste';

/**
 * The page a card's QR code opens.
 *
 * Short on purpose: it is printed on a label somebody may type in by hand, and
 * it joins the bare-shell list in `__root.tsx` so a stranger's phone gets the
 * form rather than the sidebar and never waits on IndexedDB.
 */
export const TASTE_PAGE_PREFIX = '/t/';

/**
 * Everything the Feedback page needs for one beer, in one call.
 *
 * The kind is a path segment rather than a query parameter or a header because
 * it is what selects the route: the Worker registers the same four handlers
 * under `/batch/`, `/blend/` and `/journal/`, so the URL says which beer this is about
 * in the same breath as which one. @see FeedbackSubject
 */
export function subjectScoresheetsPath(subject: FeedbackSubject): string {
  return `${SCORESHEETS_BASE_PATH}/${subject.kind}/${encodeURIComponent(subject.id)}`;
}

/** Page 1 of a new scan: the upload that creates the scan row. */
export function subjectScansPath(subject: FeedbackSubject): string {
  return `${subjectScoresheetsPath(subject)}/scans`;
}

/** Every page after the first. */
export function scanPagesPath(scanId: string): string {
  return `${SCORESHEETS_BASE_PATH}/scans/${encodeURIComponent(scanId)}/pages`;
}

/** Ask the model to read the pages (S2). */
export function scanReadPath(scanId: string): string {
  return `${SCORESHEETS_BASE_PATH}/scans/${encodeURIComponent(scanId)}/read`;
}

/** One stored page image, 1-based, served through the Worker like a batch photo. */
export function scanPagePath(scanId: string, page: number): string {
  return `${SCORESHEETS_BASE_PATH}/scans/${encodeURIComponent(scanId)}/pages/${Math.floor(page)}`;
}

/** One scan: its pages, and every sheet that came out of it. */
export function scanPath(scanId: string): string {
  return `${SCORESHEETS_BASE_PATH}/scans/${encodeURIComponent(scanId)}`;
}

/** One sheet: `PATCH` to correct a reading, `DELETE` to drop it. */
export function scoresheetPath(id: string): string {
  return `${SCORESHEETS_BASE_PATH}/${encodeURIComponent(id)}`;
}

/** Mark every unseen sheet on one beer as seen, the way opening an inbox does. */
export function subjectSeenPath(subject: FeedbackSubject): string {
  return `${subjectScoresheetsPath(subject)}/seen`;
}

/** The unseen count per beer, for the badge on the batch list. */
export function unseenScoresheetsPath(): string {
  return `${SCORESHEETS_BASE_PATH}/unseen`;
}

/** Mint (or return) the live link for each of a print run's beers. */
export function feedbackLinksPath(): string {
  return FEEDBACK_LINKS_BASE_PATH;
}

/** Stop collecting on one link. The row stays; a later print mints a fresh one. */
export function feedbackLinkRevokePath(token: string): string {
  return `${FEEDBACK_LINKS_BASE_PATH}/${encodeURIComponent(token)}/revoke`;
}

/** The public pair: `GET` reads the card, `POST` sends a sheet. */
export function tastePath(token: string): string {
  return `${TASTE_PUBLIC_BASE_PATH}/${encodeURIComponent(token)}`;
}

/** The page a taster opens. */
export function tastePagePath(token: string): string {
  return `${TASTE_PAGE_PREFIX}${encodeURIComponent(token)}`;
}

/** The absolute URL printed on a card, under its QR code. */
export function tastePageUrl(token: string, origin: string): string {
  return `${origin.replace(/\/+$/, '')}${tastePagePath(token)}`;
}

// ---------------------------------------------------------------------------
// The link
// ---------------------------------------------------------------------------

/**
 * How long a feedback token is, in bytes of entropy.
 *
 * 32 bytes — 256 bits — rendered as 43 url-safe base64 characters, the same
 * construction as a share token and an ingest token, and for the same reason:
 * the token is the entire authorization, it is printed on a piece of paper that
 * gets handed to strangers, and guessing one must be out of the question.
 */
export const FEEDBACK_TOKEN_BYTES = 32;

/**
 * The longest token string the public route will even look up.
 *
 * A bound rather than an exact length, so a future token format is not a
 * migration; anything longer is a 404 before D1 is asked.
 */
export const FEEDBACK_TOKEN_MAX_LENGTH = 128;

/**
 * Sheets one link will take in an hour, claimed in `feedback_quota`.
 *
 * **The limit does not key on an IP**, deliberately: the whole point of the
 * feature is thirty people on one club Wi-Fi behind one NAT, and an IP limit
 * would refuse the twenty-first of them. Sixty an hour is a busy meeting with
 * room to spare and is nowhere near a useful place to point a script.
 */
export const FEEDBACK_HOURLY_LIMIT = 60;

/**
 * Sheets one link will ever take. Past it, the link reads as revoked.
 *
 * A lifetime cap under the hourly one, because a token printed on a label lives
 * as long as the bottle does and nobody is watching it.
 */
export const FEEDBACK_LINK_MAX_SUBMISSIONS = 500;

/** Sheets one account may hold across every beer, of every origin. */
export const MAX_SCORESHEETS_PER_USER = 5000;

/**
 * Beers one print run may ask for at once, of either kind.
 *
 * A bottling day is a handful of beers and a sheet of labels; fifty is far past
 * anything real and is here so a malformed client cannot ask the mint route to
 * make tokens all afternoon. It lives on the protocol rather than in the Worker
 * because both ends need it: the route refuses a longer run, and the dialog
 * stops the brewer short of one and says why, rather than letting them tick
 * sixty beers and meet a 400.
 */
export const FEEDBACK_LINK_MINT_MAX = 50;

/** The largest public `POST` body the taste route will read. Measured, never declared. */
export const TASTE_BODY_MAX_BYTES = 16 * 1024;

/** The largest single scan page, after the client has rasterised and shrunk it. */
export const SCORESHEET_PAGE_MAX_BYTES = 4 * 1024 * 1024;

/** Pages one scan may carry. A competition sends one or two sheets per entry. */
export const SCORESHEET_MAX_PAGES = 12;

/** Scan bytes one account may hold in the bucket, across every batch. */
export const SCORESHEET_SCAN_MAX_BYTES_PER_USER = 250 * 1024 * 1024;

/** The card printed on a tasting label: what a taster is owed, and nothing else. */
export const feedbackCardSchema = z.strictObject({
  /** The beer's name, as the brewer wrote it. */
  name: z.string().max(120),
  /** `21A`, or `null` when the batch names no style. */
  styleCode: z.string().max(16).nullable(),
  /** `American IPA`, or `null`. */
  styleName: z.string().max(80).nullable(),
  /** Percent by volume, or `null` when the batch has no reading to give one. */
  abv: z.number().finite().min(0).max(100).nullable(),
  /**
   * A public share token for the recipe **as brewed**, or `null` — the ordinary
   * case (D-TC2).
   *
   * It names the *batch*, not the recipe, so the page it opens is the snapshot
   * this batch froze on its brew day: a taster holding a bottle is owed the beer
   * in the bottle rather than this week's edit.
   *
   * Bounded by `SHARE_TOKEN_MAX_LENGTH`, which is the same 128 the public
   * share route will look up, written as a literal rather than imported
   * because this module is the scoresheet protocol and borrowing the share
   * protocol's constant would tie two wire formats together that have no
   * other reason to agree.
   */
  recipeToken: z.string().min(1).max(128).nullable().default(null),
  /**
   * A public share token for the **blending session** this card is about, or
   * `null` — the ordinary case (PLAN-SHARE-BLEND §5).
   *
   * The same opt-in as {@link FeedbackCard.recipeToken} and never set beside
   * it: a beer on a print run is a batch, a blend or a journal, so a card
   * carries at most one of the three tokens. What it opens is the blend's own page — the beers that went
   * in, what was folded in with them, and what they came to — because a blend
   * froze no recipe on a brew day, having been made out of beers that each had
   * one.
   *
   * Bounded by the same 128 `recipeToken` is, and written out here for the same
   * reason: this module is the scoresheet protocol, and borrowing the share
   * protocol's constant would tie two wire formats together that have no other
   * reason to agree.
   */
  blendToken: z.string().min(1).max(128).nullable().default(null),
  /**
   * A public share token for the **journal** this card is about, or `null` —
   * the ordinary case (D-JL5).
   *
   * The third of the three, and set only on a journal's card, only when the
   * brewer ticked the opt-in. What it opens is the journal's own shared page at
   * `/j/:token`, which publishes more than either of the other two: every block
   * the brewer has not hidden, photos and files included. That is why the print
   * page's hint grows a sentence when a journal is on the run, and not a reason
   * for the card to carry anything different.
   *
   * Bounded by the same 128 the other two are, written out for their reason.
   */
  journalToken: z.string().min(1).max(128).nullable().default(null),
});

/**
 * What the public form shows above the sheet.
 *
 * Captured when the link is minted rather than read live: the anonymous path
 * must not have to open the owner's Durable Object, and the gallery's listing
 * rows are the precedent. Reprinting a card refreshes the copy.
 *
 * Name, style and ABV, and deliberately nothing else — style is what lets a
 * judge score to style and ABV is what a taster is owed before they drink it.
 * The grain bill is the brewer's.
 *
 * ## The one exception, and it is the brewer's to make (D-TC2)
 *
 * {@link FeedbackCard.recipeToken} opens the public share page on the batch's
 * frozen recipe, {@link FeedbackCard.blendToken} opens a blending session's own
 * page (PLAN-SHARE-BLEND §5), and {@link FeedbackCard.journalToken} opens a
 * journal's shared page (D-JL5). Each is present only when the brewer ticked
 * "Let tasters see what is in it" on the print page, and at most one of the
 * three is ever set: a beer is a batch, a blend or a journal. It is captured at
 * mint like the rest of the card, so reprinting with the option changed is what
 * updates it — and because minting is idempotent and refreshes `card_json`,
 * toggling the option and letting the page re-mint is the whole update path. A
 * row written before these fields existed carries none of the keys, and reads
 * back as `null` for each: the default is what makes an old card an ordinary
 * card rather than an unreadable one.
 *
 * ## A journal's card has no style (D-JL4)
 *
 * `styleCode` and `styleName` are both `null` on a journal's card, which the
 * schema has always allowed: a batch with no style had the same two nulls. The
 * line under the name is then the ABV alone, or nothing at all.
 */
export type FeedbackCard = z.infer<typeof feedbackCardSchema>;

/** One live (or retired) collecting link, as its owner sees it. */
export interface FeedbackLink {
  /** The bearer token. Whoever holds it can send this beer a sheet. */
  readonly token: string;
  /** Which kind of document {@link FeedbackLink.batchId} names (D-BS6). */
  readonly subject: FeedbackSubjectKind;
  /** The subject's document id: a batch's, or a blend's. @see FeedbackSubject */
  readonly batchId: string;
  /** The beer as the card describes it, frozen at mint. */
  readonly card: FeedbackCard;
  /** When it was minted, **ms since epoch** (the column is seconds). */
  readonly createdAt: number;
  /** When collecting stopped, **ms since epoch**, or `null` while it is live. */
  readonly revokedAt: number | null;
  /** Sheets sent through it, ever. Counts towards {@link FEEDBACK_LINK_MAX_SUBMISSIONS}. */
  readonly submissionCount: number;
  /** The last one, **ms since epoch**, or `null` when nobody has written yet. */
  readonly lastSubmissionAt: number | null;
}

// ---------------------------------------------------------------------------
// The sheet
// ---------------------------------------------------------------------------

/** The longest one section comment may be. A judge's box is smaller than this. */
export const SCORESHEET_COMMENT_MAX_LENGTH = 1000;

/** The longest the closing notes may be. */
export const SCORESHEET_NOTES_MAX_LENGTH = 2000;

/** Field paths one scan may name as doubtful, and how long a path may be. */
export const SCORESHEET_MAX_UNCERTAIN = 40;
const UNCERTAIN_PATH_MAX_LENGTH = 60;

/**
 * One section's score and the words beside it.
 *
 * Half points, because judges award them, and `null` for a section nobody
 * filled in — the same two rules `batchTastingSchema` states, and the maximum
 * is imported from beside them rather than written again here.
 */
function sectionSchema(max: number) {
  return z.strictObject({
    score: z.number().finite().min(0).max(max).multipleOf(0.5).nullable(),
    comment: z.string().max(SCORESHEET_COMMENT_MAX_LENGTH),
  });
}

/**
 * The five sections, keyed the way the sheet prints them.
 *
 * `satisfies` rather than a mapped type so the schema stays statically typed
 * per key, while a section added to `BATCH_TASTING_SECTIONS` becomes a
 * compile error here rather than a field that silently stops being validated.
 */
const sectionsShape = {
  aroma: sectionSchema(BATCH_TASTING_SECTION_MAX.aroma),
  appearance: sectionSchema(BATCH_TASTING_SECTION_MAX.appearance),
  flavor: sectionSchema(BATCH_TASTING_SECTION_MAX.flavor),
  mouthfeel: sectionSchema(BATCH_TASTING_SECTION_MAX.mouthfeel),
  overall: sectionSchema(BATCH_TASTING_SECTION_MAX.overall),
} satisfies Record<BatchTastingSection, z.ZodType>;

/** One of the three 1–5 rows at the foot of the 2021 sheet, or an empty row. */
const scaleSchema = z.number().int().min(1).max(5).nullable();

/**
 * A sheet, whatever filled it in.
 *
 * ## `total` is stored only where it is a fact, not a sum
 *
 * On a **quick** sheet it is the taster's number and the only score they gave.
 * On a **scan** it is what the judge *wrote* in the total box, kept as written,
 * because a judge's total that disagrees with their sections is a real thing
 * that happens and the review UI has to be able to show it. On a **full** form
 * it is `null` and {@link scoresheetScore} derives it from the sections, exactly
 * as `batchTastingTotal` does for the brewer's own sheet.
 *
 * ## `uncertain` is the reader's own doubt, not the taster's
 *
 * Scan only: the field paths the model was unsure of, so the review UI can mark
 * them and the owner can look at the photograph rather than at a confident
 * number nobody checked. The owner's `PATCH` clears it — a sheet a human has
 * confirmed has no doubtful fields left in it.
 */
export const scoresheetBodySchema = z.strictObject({
  /** Which form this came off. Set by the server from the request, never trusted. */
  form: z.enum(['quick', 'full', 'scan']),
  taster: z.strictObject({
    /** Optional on every form; the owner sees "Anonymous" when it is blank. */
    name: z.string().max(60).nullable(),
    bjcpId: z.string().max(16).nullable(),
    /** As written: "Certified", "National". Not an enum — ranks change. */
    rank: z.string().max(40).nullable(),
  }),
  event: z.strictObject({
    competition: z.string().max(120).nullable(),
    entryNumber: z.string().max(20).nullable(),
    /** "21A American IPA", as written on the sheet, not resolved to the catalog. */
    styleEntered: z.string().max(80).nullable(),
  }),
  /** The bottle-inspection box, or `null` on a form that does not ask. */
  bottleInspection: z.boolean().nullable(),
  sections: z.strictObject(sectionsShape),
  /**
   * The boxes ticked along the foot of the sheet. A descriptor a judge wrote
   * that is not one of the eighteen goes into a section comment as text; the
   * enum is the sheet's own list and does not grow per feature.
   */
  descriptors: z.array(batchTastingDescriptorSchema).max(BATCH_TASTING_DESCRIPTORS.length),
  scales: z.strictObject({
    /** 5 = a classic example, 1 = not to style. */
    stylisticAccuracy: scaleSchema,
    /** 5 = flawless, 1 = significant flaws. */
    technicalMerit: scaleSchema,
    /** 5 = wonderful, 1 = lifeless. */
    intangibles: scaleSchema,
  }),
  /** The number in the total box, where that is a fact rather than a sum. */
  total: z.number().finite().min(0).max(BATCH_TASTING_MAX_TOTAL).multipleOf(0.5).nullable(),
  notes: z.string().max(SCORESHEET_NOTES_MAX_LENGTH),
  /** Scan only: field paths the reader was unsure of. @see scoresheetBodySchema */
  uncertain: z.array(z.string().max(UNCERTAIN_PATH_MAX_LENGTH)).max(SCORESHEET_MAX_UNCERTAIN),
});

/** @see scoresheetBodySchema */
export type ScoresheetBody = z.infer<typeof scoresheetBodySchema>;

/** The two forms a stranger may fill in. The third, `scan`, is the owner's own upload. */
export const PUBLIC_SHEET_FORMS = ['quick', 'full'] as const;

/** @see PUBLIC_SHEET_FORMS */
export type PublicSheetForm = (typeof PUBLIC_SHEET_FORMS)[number];

/**
 * The quick form: one number, and no sections.
 *
 * Both halves are checked, because a body that carried both would be a sheet
 * claiming to be two things — and the section scores would silently win in
 * {@link scoresheetScore}, which is not what a slider on a phone meant.
 */
export const quickSheetSchema = scoresheetBodySchema.refine(
  (body) =>
    body.total !== null && Object.values(body.sections).every((section) => section.score === null),
  { message: 'A quick sheet is a total and no sections' },
);

/**
 * The full form: five sections, and no total of its own.
 *
 * The client derives the total live as the taster fills the sections in, and
 * storing what it displayed beside the numbers that produced it is the one
 * mistake `batchTastingSchema` exists to refuse.
 */
export const fullSheetSchema = scoresheetBodySchema.refine((body) => body.total === null, {
  message: 'A full sheet derives its total from its sections',
});

/**
 * A read scan, which may carry both and neither refinement.
 *
 * The unrefined body under a name that says why: what a judge wrote is what is
 * stored, and the owner's `PATCH` is validated by this one for the same reason.
 */
export const scanSheetSchema = scoresheetBodySchema;

/** One stored sheet, as its owner sees it. */
export interface Scoresheet {
  /** `ss-` + uuid. */
  readonly id: string;
  /** Which kind of document {@link Scoresheet.batchId} names (D-BS6). */
  readonly subject: FeedbackSubjectKind;
  /** The subject's document id: a batch's, or a blend's. @see FeedbackSubject */
  readonly batchId: string;
  /** Where it came from: a page the owner scanned, or the public form. */
  readonly origin: 'scan' | 'form';
  /** The scan it was read out of, when `origin` is `scan`. */
  readonly scanId: string | null;
  /** The link it arrived through, when `origin` is `form`. */
  readonly linkToken: string | null;
  /** The tasting or judging day, **ms since epoch**. */
  readonly at: number;
  /** When the row was written, **ms since epoch**. */
  readonly createdAt: number;
  /**
   * When a human confirmed the reading, **ms since epoch**, or `null`.
   *
   * A collected sheet is reviewed the moment it arrives — a person typed it. A
   * scan is `null` until the owner has looked at the photograph beside what the
   * model made of it, which is what "Check this reading" resolves.
   */
  readonly reviewedAt: number | null;
  /** When the owner opened the Feedback page on it, **ms since epoch**, or `null`. */
  readonly seenAt: number | null;
  readonly body: ScoresheetBody;
}

/** How far a scan got. `pending` is a scan uploaded but not yet read. */
export type ScanExtraction = 'pending' | 'ok' | 'unreadable' | 'unavailable';

/** One uploaded scan: the pages, and what reading them produced. */
export interface ScoresheetScan {
  /** `sc-` + uuid. Also the penultimate segment of the R2 key. */
  readonly id: string;
  /** Which kind of document {@link ScoresheetScan.batchId} names (D-BS6). */
  readonly subject: FeedbackSubjectKind;
  /** The subject's document id: a batch's, or a blend's. @see FeedbackSubject */
  readonly batchId: string;
  /** Pages stored, 1..N. @see SCORESHEET_MAX_PAGES */
  readonly pages: number;
  /** All pages together. Counts towards {@link SCORESHEET_SCAN_MAX_BYTES_PER_USER}. */
  readonly bytes: number;
  /** What the first stage read off the pages, markdown, all pages together (S2). */
  readonly transcript: string;
  readonly extraction: ScanExtraction;
  /** When it was uploaded, **ms since epoch**. */
  readonly createdAt: number;
}

// ---------------------------------------------------------------------------
// Refusals, and the gate
// ---------------------------------------------------------------------------

/**
 * A refusal from one of the owner's routes. `error` is a stable code.
 *
 * `plan_required` is the one worth naming here rather than in prose: collecting
 * and reading sheets needs a **paid plan** (spec, Decisions), and the
 * client turns that code into the upgrade affordance rather than into an error.
 * The code does not say which tier is required and does not need to —
 * {@link planAllowsFeedback} is the single place that says which, and every
 * client surface names the tier from that function rather than from the wire.
 */
export interface ScoresheetErrorResponse {
  readonly ok: false;
  readonly error:
    | 'plan_required'
    | 'not_found'
    | 'invalid_request'
    | 'invalid_sheet'
    | 'too_many_sheets'
    | 'too_many_pages'
    | 'page_too_large'
    | 'unsupported_image'
    | 'scan_quota'
    /** The deployment has no scans bucket bound. Staging, and only staging. */
    | 'scans_not_configured'
    /** The deployment has no vision model routed, so a scan cannot be read. */
    | 'scan_unavailable';
}

/**
 * What `GET /api/taste/:token` answers for a live link.
 *
 * ## `subject` is optional on read, and absent means a batch (D-JL2)
 *
 * The kind is a fact about the *link*, not about the card: the card is what
 * the brewer printed and is frozen at mint, while the link row has known its
 * `subject` since `0033`. So the kind rides beside the card rather than inside
 * it, and a card minted before this field existed still answers correctly —
 * the Worker reads the word off the row, not out of `card_json`.
 *
 * Optional here, though every Worker from D-JL2 on sends it, because for the
 * minutes a deploy takes a new page can be talking to an old Worker, and a
 * page cached on a taster's phone can outlive both. A response without the key
 * is read as `'batch'`, which is what every link was until blends arrived and
 * what the form was built for. {@link publicSubjectOf} is that rule, once, so
 * the page does not have to restate it.
 */
export interface TasteCardResponse {
  readonly ok: true;
  readonly card: FeedbackCard;
  readonly forms: readonly PublicSheetForm[];
  /** Which kind of document this link collects for. @see publicSubjectOf */
  readonly subject?: FeedbackSubjectKind;
}

/**
 * The kind a public card's response names, or `'batch'` when it names none
 * (D-JL2).
 *
 * Takes the response as the page decoded it — `unknown` fields and all —
 * because it is read off the wire before anything has vouched for it. A word
 * the enum does not know reads as a batch as well: the form a batch gets is
 * the one every taster was given before this field existed, and it is a
 * better guess at an unknown kind than a form with the beer questions taken
 * out.
 */
export function publicSubjectOf(response: { readonly subject?: unknown }): FeedbackSubjectKind {
  const parsed = feedbackSubjectKindSchema.safeParse(response.subject);
  return parsed.success ? parsed.data : 'batch';
}

/**
 * A refusal from the public form. Real status codes, because the caller is a
 * person with a screen rather than firmware — the opposite of the ingest
 * route's "answer 200 to almost everything".
 *
 * Unknown, revoked, capped and a D1 failure are all one byte-identical
 * `not_found`, the share-link rule: a stranger holding a token learns whether
 * it works, and nothing else.
 */
export interface TasteErrorResponse {
  readonly ok: false;
  readonly error: 'not_found' | 'invalid_sheet' | 'too_large' | 'rate_limited' | 'too_many_sheets';
}

/**
 * Whether a plan may collect, scan and read tasting sheets (spec, Decisions).
 *
 * **Any paid plan.** This was Beerwright-only until 2026-10-11, when it moved
 * down to Brewmaster. The sheets live on our server rather than in the
 * local-only product, which is why the free tier does not get them.
 *
 * What is *not* gated is the public form: a live link keeps accepting sheets
 * after a subscription lapses, the rows are held, and the owner sees them the
 * day they are back on the tier. The taster is not the customer, and a dead form
 * on a bottle embarrasses the brewer in front of their club. What a lapse stops
 * is minting, scanning and reading, which are the owner's own routes.
 *
 * One function, imported by the Worker's gate and by every client surface that
 * has to name the tier, so the server's 403 and the copy beside it cannot drift.
 */
export function planAllowsFeedback(plan: Plan): boolean {
  return plan !== 'trial';
}
